/**
 * `CertificationLevelsView` — vista "Certificazioni" della dashboard "Livelli".
 *
 * Mostra, per ogni certificazione del catalogo, quante persone la possiedono e
 * le skill collegate. I dati provengono da `GET /api/certifications/stats`
 * (vista aggregata riservata alla sezione "Livelli"). La tabella è filtrabile
 * per nome/ente ed è riordinabile lato client.
 *
 * È un componente isolato per non appesantire `CatalogLevels`: quest'ultimo lo
 * monta come quarta tab, abilitando la query solo quando la vista è attiva.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type {
  CertificationCoverageDto,
  CertificationLevelsDto,
  UserCertificationDto,
} from '../shared/dto';
import {
  Alert,
  DataTable,
  EmptyState,
  Field,
  Icon,
  Loading,
  SegmentedControl,
  TextInput,
  type Column,
  type SegmentedOption,
} from './ui';
import {
  CertificationCard,
  sortCertificationsByIssuedDesc,
} from './CertificationCard';

/**
 * Valore del filtro per ente:
 * - `'all'`: tutti gli enti;
 * - `<issuer>`: solo le certificazioni di quell'ente.
 */
type IssuerFilter = 'all' | string;

/**
 * Valore del filtro per tipo di certificazione:
 * - `'all'`: tutte;
 * - `'company'`: solo le aziendali;
 * - `'personal'`: solo le personali.
 */
type KindFilter = 'all' | 'company' | 'personal';

/** Valore pubblico del filtro per tipo di certificazione. */
export type CertificationKindFilterValue = KindFilter;

/**
 * Opzioni del filtro per tipo (grouped button, mostrato accanto al titolo della
 * pagina). L'opzione "Tutte" non è un pulsante: è lo stato di riposo quando
 * nessun tipo è selezionato. Si commuta tra "Aziendali" e "Personali" e si
 * torna a "Tutte" ri-cliccando l'opzione già attiva.
 */
const KIND_FILTER_OPTIONS: readonly SegmentedOption<Exclude<KindFilter, 'all'>>[] = [
  { value: 'company', label: 'Aziendali' },
  { value: 'personal', label: 'Personali' },
];

interface CertificationKindFilterProps {
  value: KindFilter;
  onChange: (next: KindFilter) => void;
}

/**
 * Filtro per tipo di certificazione (Aziendali/Personali) come grouped button.
 * Esposto a parte per poterlo collocare nello slot `actions` dell'header della
 * pagina "Livelli". Ri-cliccando l'opzione attiva si torna a "Tutte" (nessun
 * filtro): così non serve un pulsante "Tutte" esplicito.
 */
export function CertificationKindFilter({
  value,
  onChange,
}: CertificationKindFilterProps) {
  return (
    <SegmentedControl<Exclude<KindFilter, 'all'>>
      label="Filtra per tipo di certificazione"
      options={KIND_FILTER_OPTIONS}
      // In stato "Tutte" nessuna opzione è attiva: passiamo un valore che non
      // corrisponde a nessun pulsante.
      value={value === 'all' ? ('' as Exclude<KindFilter, 'all'>) : value}
      onChange={(next) => onChange(next === value ? 'all' : next)}
      fitted={false}
      size="sm"
      allowDeselect
    />
  );
}

/**
 * Filtra le righe per tipo, ente e query testuale (nome o ente). Funzione pura.
 * L'ordine di applicazione è: tipo → ente → testo.
 */
export function filterCertifications(
  rows: readonly CertificationCoverageDto[],
  query: string,
  issuer: IssuerFilter = 'all',
  kind: KindFilter = 'all',
): CertificationCoverageDto[] {
  let result: readonly CertificationCoverageDto[] = rows;

  if (kind === 'company') {
    result = result.filter((row) => row.isCompany === true);
  } else if (kind === 'personal') {
    result = result.filter((row) => row.isCompany !== true);
  }

  if (issuer !== 'all') {
    result = result.filter((row) => (row.issuer ?? '') === issuer);
  }

  const q = query.trim().toLowerCase();
  if (q.length === 0) return [...result];
  return result.filter(
    (row) =>
      row.name.toLowerCase().includes(q) ||
      (row.issuer ?? '').toLowerCase().includes(q),
  );
}

/** Props della vista, con `enabled` per differire il fetch quando non attiva. */
interface CertificationLevelsViewProps {
  /** Quando `false`, la query non parte (tab non attiva). */
  enabled?: boolean;
  /**
   * Filtro per tipo controllato dall'esterno (il grouped button vive
   * nell'header della pagina). Se assente, il filtro non è applicato ("Tutte").
   */
  kindFilter?: KindFilter;
}

export function CertificationLevelsView({
  enabled = true,
  kindFilter = 'all',
}: CertificationLevelsViewProps) {
  const api = useApiClient();
  const [search, setSearch] = useState('');
  const [issuerFilter, setIssuerFilter] = useState<IssuerFilter>('all');

  const { data, isLoading, isError, error } = useQuery<CertificationLevelsDto>({
    queryKey: ['certifications', 'stats'],
    queryFn: () => api.get<CertificationLevelsDto>('/api/certifications/stats'),
    enabled,
  });

  const people = data?.people ?? 0;

  // Enti presenti nelle certificazioni caricate (distinti, ordinati). Solo gli
  // issuer valorizzati concorrono alle opzioni del filtro.
  const issuerOptions = useMemo(() => {
    const issuers = new Set<string>();
    for (const c of data?.certifications ?? []) {
      if (c.issuer !== undefined && c.issuer.trim() !== '') issuers.add(c.issuer);
    }
    return [...issuers].sort((a, b) =>
      a.localeCompare(b, 'it', { sensitivity: 'base' }),
    );
  }, [data?.certifications]);

  const rows = useMemo(
    () =>
      filterCertifications(data?.certifications ?? [], search, issuerFilter, kindFilter),
    [data?.certifications, search, issuerFilter, kindFilter],
  );

  const columns = useMemo<Column<CertificationCoverageDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Certificazione',
        sortable: true,
        sortAccessor: (row) => row.name.toLowerCase(),
        render: (row) => (
          <div className="coverage-name">
            {row.imageUrl !== undefined && (
              <img
                src={row.imageUrl}
                alt=""
                aria-hidden="true"
                className="certification-badge__thumb"
                width={28}
                height={28}
              />
            )}
            <Link
              to={`/stats/certifications/${encodeURIComponent(row.certificationId)}`}
              className="coverage-name__link"
              aria-label={`Apri il dettaglio di ${row.name}`}
            >
              {row.name}
              <Icon name="external" size={14} className="coverage-name__link-icon" />
            </Link>
          </div>
        ),
      },
      {
        key: 'skills',
        header: 'Skill collegate',
        render: (row) =>
          row.skillNames.length === 0 ? (
            <span className="admin-table__assoc">—</span>
          ) : (
            <span className="admin-table__assoc">{row.skillNames.join(', ')}</span>
          ),
      },
      {
        key: 'holders',
        header: 'Persone',
        sortable: true,
        sortAccessor: (row) => row.holdersCount,
        className: 'coverage-qualified-col',
        render: (row) =>
          // Le certificazioni aziendali non sono possedute da singole persone:
          // si mostra un trattino come per le skill non collegate.
          row.isCompany === true ? (
            <span className="admin-table__assoc">—</span>
          ) : (
            <span className="coverage-cell__count">
              {row.holdersCount}
              <span className="coverage-cell__total"> / {people}</span>
            </span>
          ),
      },
    ],
    [people],
  );

  if (isLoading) {
    return <Loading>Caricamento certificazioni…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare le certificazioni.';
    return <Alert tone="error">{message}</Alert>;
  }

  // Le certificazioni aziendali sono mostrate come card (come nella dashboard
  // team) invece che in tabella: non hanno possessori né skill, ma hanno le
  // proprie date di conseguimento/scadenza.
  const showCompanyCards = kindFilter === 'company';

  return (
    <div className="certification-levels-view">
      <Field
        id="certification-coverage-search"
        label="Cerca certificazione"
        hint={
          showCompanyCards
            ? 'Cerca per nome della certificazione.'
            : 'Filtra per ente e cerca per nome della certificazione.'
        }
      >
        <div className="competence-search">
          {/* Il filtro per ente non è pertinente per le aziendali: si nasconde
              quando si mostrano solo quelle. */}
          {!showCompanyCards && (
            <span className="competence-search__department user-admin__select">
              <select
                id="certification-coverage-issuer"
                aria-label="Filtra certificazioni per ente"
                value={issuerFilter}
                disabled={issuerOptions.length === 0}
                onChange={(e) => setIssuerFilter(e.target.value)}
              >
                <option value="all">Tutti gli enti</option>
                {issuerOptions.map((issuer) => (
                  <option key={issuer} value={issuer}>
                    {issuer}
                  </option>
                ))}
              </select>
            </span>
          )}
          <TextInput
            id="certification-coverage-search"
            className="competence-search__input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca una certificazione…"
            autoComplete="off"
          />
        </div>
      </Field>

      {showCompanyCards ? (
        rows.length === 0 ? (
          <EmptyState role="status">
            Non ci sono certificazioni aziendali. Aggiungile dall'area di
            amministrazione.
          </EmptyState>
        ) : (
          <ul className="certification-list">
            {sortCertificationsByIssuedDesc(
              rows,
              (row) => row.issuedAt,
              (row) => row.name,
            ).map((row) => (
              <li
                key={row.certificationId}
                className="certification-card certification-card--linked"
              >
                {/* Stretched link: copre l'intera card e apre il dettaglio. */}
                <Link
                  to={`/insights/certifications/${encodeURIComponent(row.certificationId)}`}
                  className="certification-card__stretched-link"
                  aria-label={`Apri il dettaglio di ${row.name}`}
                />
                <CertificationCard
                  certification={toUserCertification(row)}
                  showOpenIcon
                />
              </li>
            ))}
          </ul>
        )
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.certificationId}
          defaultSort={{ key: 'holders', direction: 'desc' }}
          emptyMessage="Non ci sono ancora certificazioni a catalogo. Aggiungile dall'area di amministrazione o importale da Credly."
        />
      )}
    </div>
  );
}

/**
 * Adatta una riga della vista aggregata ({@link CertificationCoverageDto}) al
 * DTO atteso da {@link CertificationCard}. Usato per le certificazioni
 * aziendali, che non hanno assegnazione utente: l'origine è "manuale", niente
 * badge Credly e nessuna skill collegata.
 */
function toUserCertification(row: CertificationCoverageDto): UserCertificationDto {
  return {
    certificationId: row.certificationId,
    name: row.name,
    ...(row.issuer !== undefined ? { issuer: row.issuer } : {}),
    ...(row.imageUrl !== undefined ? { imageUrl: row.imageUrl } : {}),
    skillIds: [],
    source: 'manual',
    issuedAt: row.issuedAt ?? null,
    expiresAt: row.expiresAt ?? null,
  };
}

export default CertificationLevelsView;
